import { z } from 'zod';
import { phoneSchema, storedPhoneSchema } from '../phone/phone.js';
export const integrationDocumentSchema = z.object({
    type: z.enum(['NATIONAL_ID', 'PASSPORT']),
    number: z.string().trim().min(1),
    country: z.string().trim().length(2),
});
/**
 * Optional traceability fields (additive, CUS-23/CUS-24): older callers may omit
 * them. Names are stored as snapshots so the history never depends on the
 * caller's current employee profile.
 */
const employeeIdSchema = z.string().trim().min(1).max(100);
const employeeNameSchema = z.string().trim().min(1).max(200);
/** POST /integration/customers (customers.md section 5). */
export const integrationRegisterCustomerSchema = z.object({
    /** Normalised and validated with the shared phone rule (customers.md section 9). */
    phone: phoneSchema,
    name: z.string().trim().min(1).max(200).optional(),
    document: integrationDocumentSchema.optional(),
    /** Employee of the calling system who registered the customer. */
    employeeId: employeeIdSchema.optional(),
    employeeName: employeeNameSchema.optional(),
    /** Receipt of a consumed phone verification (customers.md section 10.4). */
    phoneVerificationReceipt: z.string().trim().min(1).max(64).optional(),
});
/** POST /integration/movements. */
export const integrationMovementSchema = z.object({
    customerId: z.string().min(1),
    systemCode: z.string().min(1),
    branchId: z.string().min(1),
    type: z.string().min(1),
    reference: z.string().min(1),
    amount: z.string().regex(/^\d+(\.\d{1,5})?$/, 'must be a decimal amount with up to 5 places'),
    currency: z.string().min(1),
    occurredAt: z.iso.datetime({ offset: true }),
    link: z.string().url().optional(),
    /** Employee who performed the transaction in the calling system. */
    employeeId: employeeIdSchema.optional(),
    employeeName: employeeNameSchema.optional(),
    /** Outside-service (field/PDA) employee, when the operation happened outside the office. */
    fieldEmployeeName: employeeNameSchema.optional(),
});
/**
 * POST /integration/movements/{system}/{reference}/reverse body (CUS-29): the
 * whole body is optional. When the calling system sends the employee who
 * reversed the transaction, the name is stored as a snapshot. No body or an
 * empty body keeps working exactly as before.
 */
export const integrationReverseSchema = z.preprocess((value) => value ?? {}, z.object({
    employeeId: employeeIdSchema.optional(),
    employeeName: employeeNameSchema.optional(),
}));
/**
 * POST /integration/customers/{id}/phone (KAN-52): a linked system changes the
 * shared phone of a customer it is linked to. `previousPhone` is the phone the
 * calling system last saw; it is recorded in the audit row only, so a phone
 * changed meanwhile by staff here never blocks the calling system.
 */
export const integrationChangePhoneSchema = z.object({
    /**
     * Read with `storedPhoneSchema`: a phone of an excluded country is accepted
     * only when it equals the locked customer's stored phone (an idempotent
     * repeat); any change to one is refused with PHONE_COUNTRY_NOT_ALLOWED
     * (customers.md section 9.7).
     */
    phone: storedPhoneSchema,
    /**
     * Audit only: kept as sent (trimmed), because the caller's last seen value
     * may be a legacy phone that no longer passes the phone rule.
     */
    previousPhone: z.string().trim().min(1).max(32).optional(),
    /** Employee of the calling system who changed the phone (recorded in the audit log). */
    employeeId: employeeIdSchema.optional(),
    employeeName: employeeNameSchema.optional(),
    /** Receipt of a consumed CHANGE_PHONE verification (customers.md section 10.4). */
    phoneVerificationReceipt: z.string().trim().min(1).max(64).optional(),
});
/**
 * GET /integration/customers?phone= : the phone is normalised before the
 * look-up. A stored phone of an excluded country is still found
 * (customers.md section 9.7).
 */
export const integrationPhoneQuerySchema = z.object({ phone: storedPhoneSchema });
export const integrationCustomerViewSchema = z.object({
    id: z.string(),
    phone: z.string(),
    name: z.string().nullable(),
    level: z.enum(['A', 'B', 'C']),
    phoneVerifiedAt: z.string().nullable().optional(),
});
//# sourceMappingURL=contract.js.map